import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Gauge, Coins, Sun, Moon, SunMoon, Sparkles, Trash2, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BackupManager } from "@/components/BackupManager";
import { SmsLog } from "@/components/SmsLog";
import { PresentationEmailPanel } from "@/components/PresentationEmailPanel";

export default function Impostazioni() {
  const { mode: themeMode, setMode: setThemeMode, effective: themeEffective } = useTheme();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [usage, setUsage] = useState(null);
  const [demoBusy, setDemoBusy] = useState("");
  const [demoProg, setDemoProg] = useState(null); // {percent, step, status}

  const generateDemo = async () => {
    setDemoBusy("gen");
    setDemoProg({ percent: 0, step: "Avvio…", status: "running" });
    try {
      await api.post("/demo/generate");
      // polling avanzamento
      for (let i = 0; i < 600; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        let s;
        try { s = (await api.get("/demo/generate/status")).data; } catch { continue; }
        setDemoProg({ percent: s.percent || 0, step: s.step || "", status: s.status });
        if (s.status === "done") {
          toast.success("Dati demo generati: email, condomìni, pratiche AI, avvisi, sondaggi e documenti.");
          break;
        }
        if (s.status === "error") {
          toast.error("Errore durante la generazione dei dati demo" + (s.error ? `: ${s.error}` : ""));
          break;
        }
      }
    } catch { toast.error("Impossibile avviare la generazione dei dati demo"); }
    finally { setDemoBusy(""); setTimeout(() => setDemoProg(null), 1500); }
  };

  const purgeDemo = async () => {
    setDemoBusy("del");
    try {
      const r = await api.post("/demo/purge");
      const tot = (r.data.communications || 0) + (r.data.buildings || 0) + (r.data.residents || 0) + (r.data.accounts || 0);
      toast.success(tot > 0 ? "Dati demo rimossi, inclusa la casella di posta demo." : "Nessun dato demo da rimuovere.");
    } catch { toast.error("Impossibile rimuovere i dati demo"); }
    finally { setDemoBusy(""); }
  };

  useEffect(() => {
    if (!isSuper) return;
    api.get("/usage/summary?days=30").then((r) => setUsage(r.data)).catch(() => {});
  }, [isSuper]);

  return (
    <div className="fade-in" data-testid="settings-page">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Configurazione</div>
        <h1 className="font-head text-4xl font-extrabold tracking-tighter">Impostazioni</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Consumo AI e aspetto dell'applicazione.</p>
      </header>

      <div className="p-4 sm:p-10 space-y-6">
        <div data-testid="usage-counter-card" className={`bg-[#0A0A0A] text-white rounded-md p-6 ${isSuper ? "" : "hidden"}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Gauge className="h-4 w-4 text-[#4DA3FF]" />
                <h3 className="font-head text-lg font-semibold">Consumo AI (ultimi 30 giorni)</h3>
              </div>
              <p className="text-sm text-white/50">Token realmente consumati e costo stimato delle richieste all'intelligenza artificiale.</p>
            </div>
            <Link to="/consumi" data-testid="usage-detail-link" className="text-sm font-medium text-[#4DA3FF] hover:text-white transition-colors duration-150 shrink-0">
              Dettaglio completo →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">Token totali</div>
              <div data-testid="usage-total-tokens" className="font-head text-3xl font-extrabold tracking-tighter mt-1">
                {usage ? new Intl.NumberFormat("it-IT").format(usage.totals.total_tokens) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold flex items-center gap-1"><Coins className="h-3 w-3" />Costo stimato</div>
              <div data-testid="usage-total-eur" className="font-head text-3xl font-extrabold tracking-tighter mt-1 text-emerald-400">
                {usage ? `€ ${usage.totals.est_cost_eur.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : "—"}
              </div>
              <div className="text-[10px] text-white/30 mt-0.5">≈ ${usage ? usage.totals.est_cost_usd.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : "0"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">Chiamate AI</div>
              <div className="font-head text-3xl font-extrabold tracking-tighter mt-1">{usage ? new Intl.NumberFormat("it-IT").format(usage.totals.calls) : "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">Input / Output</div>
              <div className="font-head text-xl font-extrabold tracking-tighter mt-2 text-white/80">
                {usage ? `${new Intl.NumberFormat("it-IT").format(usage.totals.prompt_tokens)} / ${new Intl.NumberFormat("it-IT").format(usage.totals.completion_tokens)}` : "—"}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-white/30 mt-4">Costo indicativo (cambio ~0,92 €/$). I valori effettivi fatturati sulla Universal Key possono variare in base al modello.</p>
        </div>

        <div data-testid="demo-data-card" className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4 text-[#007BFF]" />
            <h3 className="font-head text-lg font-semibold">Dati dimostrativi</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Genera email, condomìni, <strong>pratiche AI, avvisi, sondaggi, documenti</strong> e una casella di posta demo
            di esempio per esplorare l'app. I dati demo vengono rimossi automaticamente quando colleghi una casella reale
            o inserisci dati veri. Puoi anche gestirli manualmente qui.
          </p>
          {demoProg && (
            <div className="mb-4" data-testid="demo-progress">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                <span data-testid="demo-progress-step">{demoProg.step || "In corso…"}</span>
                <span data-testid="demo-progress-percent" className="font-mono">{demoProg.percent}%</span>
              </div>
              <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${demoProg.status === "error" ? "bg-red-500" : "bg-[#007BFF]"}`}
                  style={{ width: `${Math.max(4, demoProg.percent)}%` }} />
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button data-testid="generate-demo-btn" onClick={generateDemo} disabled={!!demoBusy}
              className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-9">
              {demoBusy === "gen" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {demoBusy === "gen" ? `Generazione… ${demoProg?.percent ?? 0}%` : "Genera dati demo"}
            </Button>
            <Button data-testid="purge-demo-btn" onClick={purgeDemo} disabled={!!demoBusy} variant="outline"
              className="rounded-md h-9 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
              {demoBusy === "del" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Rimuovi dati demo
            </Button>
          </div>
        </div>

        {isSuper && <PresentationEmailPanel />}

        <BackupManager />

        <div data-testid="sms-history-card" className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="h-4 w-4 text-[#007BFF]" />
            <h3 className="font-head text-lg font-semibold">Storico SMS</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Elenco degli SMS inviati dai tuoi condomìni (credenziali portali, pratiche, avvisi e sondaggi) con data, destinatario ed esito di consegna.
          </p>
          <SmsLog endpoint="/sms/my-log" />
        </div>

        <div data-testid="theme-card" className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center gap-2 mb-1">
            <SunMoon className="h-4 w-4 text-[#007BFF]" />
            <h3 className="font-head text-lg font-semibold">Aspetto (tema chiaro/scuro)</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            In modalità <strong>Automatico</strong> l'app passa al tema scuro la sera (20:00–07:00) per affaticare meno la vista.
            {themeMode === "auto" && <> Attualmente: <strong>{themeEffective === "dark" ? "Scuro" : "Chiaro"}</strong>.</>}
          </p>
          <div className="grid grid-cols-3 gap-2 max-w-md">
            {[
              { key: "auto", label: "Automatico", icon: SunMoon },
              { key: "light", label: "Chiaro", icon: Sun },
              { key: "dark", label: "Scuro", icon: Moon },
            ].map(({ key, label, icon: Icon }) => (
              <button key={key} type="button" data-testid={`theme-option-${key}`} onClick={() => setThemeMode(key)}
                className={`flex flex-col items-center gap-1.5 border rounded-md px-3 py-3 text-sm font-medium transition-colors duration-150 ${themeMode === key ? "border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]" : "border-border text-muted-foreground hover:border-gray-400"}`}>
                <Icon className="h-5 w-5" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
